/*
|--------------------------------------------------------------------------
| Component: MediaModal.jsx
| Author: Sayed Ahmed
| Description: A modal for managing media. Supports tabbed interface for
|              viewing existing media (library) and uploading new files.
|              Allows single or multiple selection.
|--------------------------------------------------------------------------
*/

import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import Modal from "react-bootstrap/Modal";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMedia,
  resetMediaState,
} from "../../../store/custom-actions/mediaSlice";
import getImageUrl from "../../../utils/getImageUrl";
import MediaUploader from "./MediaUploader";
import api from "../../../api/axiosInstance";
import { useInView } from "react-intersection-observer";
import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";
import "../../../styles/media.css";

/**
 * LazyImage loads image only when in view (lazy loading).
 */
function LazyImage({
  src,
  alt = "",
  className = "",
  style = {},
  placeholderSrc = "/placeholder.jpg",
}) {
  const [ref, inView] = useInView({ triggerOnce: true, rootMargin: "100px" });
  const [loaded, setLoaded] = useState(false);

  return (
    <div ref={ref} style={{ position: "relative", ...style }}>
      {/* Placeholder while the image is loading */}
      <img
        src={placeholderSrc}
        alt="placeholder"
        className={className}
        style={{
          ...style,
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          borderRadius: 8,
          opacity: loaded ? 0 : 1,
          transition: "opacity 0.3s ease",
        }}
      />
      {inView && (
        <img
          src={src}
          alt={alt}
          className={className}
          style={{
            ...style,
            opacity: loaded ? 1 : 0,
            transition: "opacity 0.3s ease",
            position: "relative",
            zIndex: 1,
          }}
          onLoad={() => setLoaded(true)}
        />
      )}
    </div>
  );
}

/**
 * MediaModal displays the media manager with tabs (Library, Upload).
 * @param {'image'|'video'} [mediaType='image'] - نوع الميديا: صورة أو فيديو (يُحدد عند وضع الزر)
 */
export default function MediaModal({
  show,
  onHide,
  onSave,
  selectionType = "single",
  selectedImages = [],
  mediaType = "image",
}) {
  const [selected, setSelected] = useState([]);
  const [activeTab, setActiveTab] = useState("library");
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const media = useSelector((state) => state.media.items);
  const loading = useSelector((state) => state.media.loading);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const { ref: loadMoreRef, inView } = useInView({ triggerOnce: false });
  const [scrollDirection, setScrollDirection] = useState(null);
  const scrollContainerRef = useRef(null);

  // Reset state when opening modal, switching tabs, or changing mediaType
  useEffect(() => {
    if (show && activeTab === "library") {
      dispatch(resetMediaState());
      setPage(1);
      setHasMore(true);
    }
  }, [show, activeTab, mediaType, dispatch]);

  // Fetch media when page or mediaType changes
  useEffect(() => {
    if (show && activeTab === "library") {
      dispatch(fetchMedia({ page, type: mediaType })).then((res) => {
        const payload = res.payload;
        if (payload?.current_page >= payload?.last_page) {
          setHasMore(false);
        }
      });
    }
  }, [show, activeTab, dispatch, page, mediaType]);

  // Track selected images when modal opens
  useEffect(() => {
    if (show) {
      setSelected(selectedImages.map(String));
    }
  }, [show, selectedImages]);

  const toggleSelect = (id) => {
    if (selectionType === "single") {
      setSelected([id]);
    } else {
      setSelected((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      );
    }
  };

  const handleSave = () => {
    const selectedData = media
      .filter((img) => selected.includes(String(img.id)))
      .map((img) => ({ id: img.id, path: img.path, type: img.type }));
    onSave(selectedData);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("Are you sure you want to delete this item?"))) return;
    try {
      await api.delete(`/dashboard/uploads/${id}`);
      dispatch(resetMediaState());
      setPage(1);
      setHasMore(true);
      dispatch(fetchMedia(1));
    } catch (error) {
      console.error("Delete failed", error);
    }
  };

  return (
    <Modal show={show} onHide={onHide} fullscreen scrollable={false}>
      <Modal.Header closeButton>
        <Modal.Title>Media Library</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <Tabs
          activeKey={activeTab}
          onSelect={(key) => setActiveTab(key)}
          className="mb-3 px-3 pt-3"
        >
          <Tab eventKey="library" title="Library">
            {loading && media.length === 0 ? (
              <div className="text-center py-5">Loading...</div>
            ) : (
              <PerfectScrollbar
                onScrollY={(container) => {
                  const scrollTop = container.scrollTop;
                  const containerHeight = container.clientHeight;
                  const scrollHeight = container.scrollHeight;

                  // تحديد اتجاه التمرير
                  setScrollDirection((prev) => {
                    if (scrollTop > prev) return "down";
                    else return "up";
                  });

                  // تحميل المزيد إذا اقترب من نهاية السكول
                  const nearBottom =
                    scrollTop + containerHeight >= scrollHeight - 100;
                  if (
                    nearBottom &&
                    hasMore &&
                    show &&
                    activeTab === "library"
                  ) {
                    setPage((prev) => prev + 1);
                  }
                }}
                style={{
                  minHeight: "50vh",
                  maxHeight: "70vh",
                  padding: "1rem",
                }}
              >
                <div className="media-grid">
                  {media.map((img) => {
                    const isSelected = selected.includes(String(img.id));
                    const isVideo = img.type === "video";
                    return (
                      <div
                        key={img.id}
                        className={`media-item-wrapper ${
                          isSelected ? "selected" : ""
                        }`}
                      >
                        <label className="media-item d-block position-relative">
                          <input
                            type="checkbox"
                            className="form-check-input d-none"
                            checked={isSelected}
                            onChange={() => toggleSelect(String(img.id))}
                          />
                          {isVideo ? (
                            <div
                              className="media-img position-relative rounded overflow-hidden bg-dark d-flex align-items-center justify-content-center"
                              style={{
                                width: "100%",
                                aspectRatio: "1",
                                minHeight: 100,
                              }}
                            >
                              <video
                                src={getImageUrl(img.path)}
                                className="w-100 h-100"
                                style={{ objectFit: "cover" }}
                                muted
                                preload="metadata"
                              />
                              <i
                                className="bi bi-play-circle-fill position-absolute text-white"
                                style={{ fontSize: "2rem", opacity: 0.9 }}
                              />
                            </div>
                          ) : (
                            <LazyImage
                              src={getImageUrl(img.path)}
                              alt=""
                              className="media-img"
                              placeholderSrc="/placeholder.jpg"
                            />
                          )}
                          <button
                            className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                            style={{ zIndex: 5 }}
                            onClick={(e) => {
                              e.preventDefault();
                              handleDelete(img.id);
                            }}
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </label>
                      </div>
                    );
                  })}
                  {hasMore && <div ref={loadMoreRef} style={{ height: 50 }} />}
                </div>
              </PerfectScrollbar>
            )}
          </Tab>

          <Tab eventKey="upload" title="Upload">
            <div className="px-3 pt-3">
              <MediaUploader
                mediaType={mediaType}
                onUploaded={() => {
                  dispatch(resetMediaState());
                  setPage(1);
                  setHasMore(true);
                  dispatch(fetchMedia({ page: 1, type: mediaType }));
                }}
              />
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-secondary" onClick={onHide}>
          {t("Close")}
        </button>
        <button className="btn btn-primary" onClick={handleSave}>
          {t("Save")}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
